<script setup lang="ts">
// types
import type { HeaderMobileItemProps } from '@composables/widgets/headers/header-mobile-item.hook'
// constants
import { STRAPI_HEADER_ITEM_TYPE } from '@ts-strapi/enums'
// hooks
import { useHeaderMobileItem } from '@composables/widgets/headers/header-mobile-item.hook'
// assets
import { faArrowRight, faChevronDown } from '@fortawesome/pro-solid-svg-icons'
// components
import { AppText } from '@UI/Typography/AppText'
import { AppIcon } from '@UI/Icons/AppIcon'
import { LinkBase } from '@UI/Links/LinkBase'

defineOptions({
  name: 'HeaderMobileItem',
})

const props = defineProps<HeaderMobileItemProps>()

const emit = defineEmits<{
  'click:expand': []
  'click:collapse': []
}>()

const {
  refIsMega,
  handleToggle,
} = useHeaderMobileItem({
  props,
  onExpand: () => emit('click:expand'),
  onCollapse: () => emit('click:collapse'),
})
</script>

<template>
  <div
    class="header-mobile-item"
    :class="{ 'header-mobile-item--expanded': isExpanded }"
  >
    <LinkBase
      v-if="item.__component === STRAPI_HEADER_ITEM_TYPE.SimpleLink"
      :link="{ label: item.label, url: item.url, newTab: item.newTab }"
      class="header-mobile-item__label"
    >
      <AppText
        tag="span"
        name="body2-medium"
        color="slate-900"
      >
        {{ item.label }}
      </AppText>
    </LinkBase>

    <button
      v-else
      type="button"
      class="header-mobile-item__label header-mobile-item__label--mega"
      :aria-expanded="isExpanded"
      @click="handleToggle"
    >
      <AppText
        tag="span"
        name="body2-medium"
        color="slate-900"
      >
        {{ item.label }}
      </AppText>
      <AppIcon
        class="header-mobile-item__chevron"
        :icon="faChevronDown"
        size="medium"
      />
    </button>

    <div
      v-if="refIsMega && isExpanded"
      class="header-mobile-item__content"
    >
      <template v-if="item.__component === STRAPI_HEADER_ITEM_TYPE.MultipleMenu">
        <AppText
          class="header-mobile-item__heading"
          tag="p"
          name="body3-medium"
          color="slate-900"
        >
          {{ item.subTitle }}
        </AppText>

        <section
          v-for="col in item.columns"
          :key="col.titleLink.url"
          class="header-mobile-item__group"
        >
          <LinkBase
            :link="col.titleLink"
            class="header-mobile-item__group-title"
          >
            <AppText
              tag="span"
              name="body4-medium"
              color="slate-500"
            >
              {{ col.titleLink.label }}
            </AppText>
          </LinkBase>
          <ul class="header-mobile-item__links">
            <li
              v-for="link in col.links"
              :key="link.url"
            >
              <LinkBase :link="link">
                <AppText
                  tag="span"
                  name="body3"
                  color="slate-700"
                >
                  {{ link.label }}
                </AppText>
              </LinkBase>
            </li>
          </ul>
          <LinkBase
            v-if="item.seeAllLink"
            :link="item.seeAllLink"
            class="header-mobile-item__see-all"
          >
            <AppText
              tag="span"
              name="body2-medium"
              color="indigo-500"
            >
              {{ item.seeAllLink.label }}
            </AppText>
            <AppIcon
              :icon="faArrowRight"
              size="medium"
              color="indigo-500"
            />
          </LinkBase>
        </section>

        <AppText
          class="header-mobile-item__heading"
          tag="p"
          name="body3-medium"
          color="slate-900"
        >
          {{ item.programTitle }}
        </AppText>

        <ul class="header-mobile-item__links">
          <li
            v-for="link in item.programLinks"
            :key="link.url"
          >
            <LinkBase :link="link">
              <AppText
                tag="span"
                name="body3"
                color="slate-700"
              >
                {{ link.label }}
              </AppText>
            </LinkBase>
          </li>
        </ul>
      </template>

      <template v-else-if="item.__component === STRAPI_HEADER_ITEM_TYPE.SingleMenu">
        <section
          v-for="block in item.blocks"
          :key="block.titleLink.url"
          class="header-mobile-item__group"
        >
          <LinkBase
            :link="block.titleLink"
            class="header-mobile-item__group-title"
          >
            <AppText
              tag="span"
              name="body3-medium"
              color="slate-900"
            >
              {{ block.titleLink.label }}
            </AppText>
          </LinkBase>
          <ul class="header-mobile-item__links">
            <li
              v-for="link in block.links"
              :key="link.url"
            >
              <LinkBase :link="link">
                <AppText
                  tag="span"
                  name="body3"
                  color="slate-700"
                >
                  {{ link.label }}
                </AppText>
              </LinkBase>
            </li>
          </ul>
          <LinkBase
            v-if="block.seeAllLink"
            :link="block.seeAllLink"
            class="header-mobile-item__see-all"
          >
            <AppText
              tag="span"
              name="body2-medium"
              color="indigo-500"
            >
              {{ block.seeAllLink.label }}
            </AppText>
            <AppIcon
              :icon="faArrowRight"
              size="medium"
              color="indigo-500"
            />
          </LinkBase>
        </section>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss" src="@scss/components/Widgets/Headers/HeaderMobileItem.scss" />
